import { Button } from '@storeblocks/button';
import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { Order } from '@/api/orderList/models/Order';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { defaultDateFormat } from '@/i18n/hooks/useUserDateFormat';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { orderExpiryDate } from '@/util/orderExpiryDate';

import { CheckIcon } from './CheckIcon';
import { WaitingIcon } from './WaitingIcon';

interface Props {
  order: Order;
}

export const SignatoriesTable: FC<Props> = ({ order }) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const allSigned = order.signatories.every(
    (signatories) => signatories.signedDate,
  );

  return (
    <WithGap gap="16">
      <h3>{texts.pages.orderList.signatures.title}</h3>

      {/* Only display the expiry date when there is a missing signature. */}
      {!allSigned && (
        <div className="font-details">
          {fmsWithTemplate(texts.pages.orderList.expiryDate, {
            dateTime: format(
              orderExpiryDate(order.createdDate),
              `${defaultDateFormat} HH:mm`,
            ),
          })}
        </div>
      )}

      <Table>
        <tbody>
          {order.signatories.map((signatory) => (
            <tr key={signatory.name}>
              <td>
                <span className="font-body">{signatory.name}</span>
              </td>
              <td>
                <StyledWithGap direction="row" gap="8">
                  {signatory.signedDate ? (
                    <>
                      <span className="font-body">
                        {texts.pages.orderList.signatures.signed}
                      </span>
                      <CheckIcon />
                    </>
                  ) : (
                    <>
                      <span className="font-body">
                        {texts.pages.orderList.signatures.notSigned}
                      </span>
                      <WaitingIcon />
                    </>
                  )}
                </StyledWithGap>
              </td>
              <td>
                {signatory.signedDate && (
                  <span className="font-body">
                    {format(signatory.signedDate, `${defaultDateFormat} HH:mm`)}
                  </span>
                )}

                {!signatory.signedDate && signatory.signUrl && (
                  <Button
                    iconRight="edit"
                    as="a"
                    href={signatory.signUrl}
                    data-trackid={getTrackId(
                      'go-to-signicat',
                      TrackingElement.Link,
                    )}
                  >
                    {texts.pages.orderList.signatures.signOrder}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </WithGap>
  );
};

const Table = styled.table`
  border-spacing: 0 8px;

  tr td:last-of-type {
    text-align: right;
  }
`;

const StyledWithGap = styled(WithGap)`
  align-items: baseline;
`;
